import React from 'react';
import type { ViewStyle } from 'react-native';
import { View, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';

import { theme } from '@garnish/constants';
import { clamp } from '../../utils';
import { useProgressBarStyle } from './LinearProgressBar.hooks';

export type LinearProgressBarProps = {
  readonly progress: number;
  readonly animated?: boolean;
  readonly testID?: string;
  readonly progressStyles?: ViewStyle;
  readonly barStyles?: ViewStyle;
};

export const LinearProgressBar = (props: LinearProgressBarProps) => {
  const { progress, animated, progressStyles, barStyles, testID } = props;

  const clampedProgress = clamp(progress, MIN_VALUE, MAX_VALUE);
  const a11yValue = Number(clampedProgress.toFixed(2));

  const { style, onLayout } = useProgressBarStyle(clampedProgress, animated);

  return (
    <View style={[styles.bar, barStyles]} onLayout={onLayout} testID={testID}>
      <Animated.View
        accessibilityValue={{
          min: MIN_VALUE,
          max: MAX_VALUE,
          now: a11yValue,
        }}
        accessibilityRole="progressbar"
        style={[styles.progressBar, progressStyles, style]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    flexGrow: 1,
    height: 6,
    backgroundColor: theme.colors.GRAY,
    borderRadius: theme.radius.large,
    overflow: 'hidden',
  },
  progressBar: {
    flexGrow: 1,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.KALE,
  },
});

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const MIN_VALUE = 0;
const MAX_VALUE = 100;
